<template>
  <div class="tw-w-full">
    <!-- Main container for calendar accounts -->
    <div
      class="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-3"
    >
      <!-- Existing calendar accounts -->
      <div
        v-for="(account, key) in calendarAccounts"
        :key="key"
        class="tw-flex tw-flex-col tw-gap-4 tw-rounded-lg tw-border tw-border-light-gray-stroke tw-bg-white tw-p-4"
      >
        <CalendarAccount
          :syncWithBackend="syncWithBackend"
          :toggleState="toggleState"
          :account="account"
          :eventId="eventId"
          :calendarEventsMap="calendarEventsMapCopy"
          :removeDialog="removeDialog"
          :selectedRemoveEmail="removePayload.email"
          :fillSpace="fillSpace"
          @toggleCalendarAccount="
            (payload) => $emit('toggleCalendarAccount', payload)
          "
          @toggleSubCalendarAccount="
            (payload) => $emit('toggleSubCalendarAccount', payload)
          "
          @openRemoveDialog="openRemoveDialog"
        />
      </div>

      <!-- Add Calendar Button Card -->
      <div
        v-if="allowAddCalendarAccount"
        class="hover:tw-bg-gray-50 tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border-2 tw-border-dashed tw-border-light-gray-stroke tw-bg-white tw-p-6 tw-transition-colors"
      >
        <v-dialog
          v-model="addCalendarAccountDialog"
          width="400"
          content-class="tw-m-0"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              text
              color="primary"
              class="tw-flex tw-flex-col tw-gap-1 tw-normal-case"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-plus</v-icon>
              <span class="tw-text-sm">Add calendar</span>
            </v-btn>
          </template>
          <CalendarTypeSelector
            @addGoogleCalendar="addGoogleCalendar"
            @addedAppleCalendar="addedAppleCalendar"
            @addOutlookCalendar="addOutlookCalendar"
          />
        </v-dialog>
      </div>
    </div>

    <v-dialog v-model="removeDialog" width="500" persistent>
      <v-card>
        <v-card-title>Are you sure?</v-card-title>
        <v-card-text class="tw-text-sm tw-text-dark-gray"
          >Are you sure you want to remove
          {{ removePayload.email }}?</v-card-text
        >
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="removeDialog = false">Cancel</v-btn>
          <v-btn text color="error" @click="removeAccount">Remove</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex"
import { authTypes, calendarTypes } from "@/constants"
import {
  get,
  post,
  _delete,
  signInGoogle,
  signInOutlook,
  getCalendarAccountKey,
} from "@/utils"
import CalendarAccount from "@/components/settings/CalendarAccount.vue"
import CalendarTypeSelector from "@/components/settings/CalendarTypeSelector.vue"

export default {
  name: "CalendarAccounts",

  props: {
    toggleState: { type: Boolean, default: false },
    eventId: { type: String, default: "" },
    calendarEventsMap: { type: Object, default: () => {} },
    syncWithBackend: { type: Boolean, default: true },
    allowAddCalendarAccount: { type: Boolean, default: true },
    initialCalendarAccountsData: { type: Object, default: () => {} },
    fillSpace: { type: Boolean, default: false },
  },

  data: () => ({
    removeDialog: false,
    removePayload: {},
    addCalendarAccountDialog: false,
    calendarAccounts: {},
    calendarEventsMapCopy: {},
  }),

  computed: {
    ...mapState(["authUser"]),
  },

  mounted() {
    this.calendarAccounts = !this.initialCalendarAccountsData
      ? this.authUser.calendarAccounts
      : this.initialCalendarAccountsData
  },

  methods: {
    ...mapActions(["showError", "showInfo", "refreshAuthUser"]),
    ...mapMutations(["setAuthUser"]),
    addGoogleCalendar() {
      signInGoogle({
        state: {
          type: this.toggleState
            ? authTypes.ADD_CALENDAR_ACCOUNT_FROM_EDIT
            : authTypes.ADD_CALENDAR_ACCOUNT,
          eventId: this.eventId,
          calendarType: calendarTypes.GOOGLE,
        },
        requestCalendarPermission: true,
        selectAccount: true,
      })
    },
    addedAppleCalendar() {
      this.addCalendarAccountDialog = false
      this.calendarAccounts = this.authUser.calendarAccounts
    },
    addOutlookCalendar() {
      signInOutlook({
        state: {
          type: this.toggleState
            ? authTypes.ADD_CALENDAR_ACCOUNT_FROM_EDIT
            : authTypes.ADD_CALENDAR_ACCOUNT,
          eventId: this.eventId,
          calendarType: calendarTypes.OUTLOOK,
        },
        requestCalendarPermission: true,
      })
    },
    openRemoveDialog(payload) {
      this.removeDialog = true
      this.removePayload = payload
    },
    removeAccount() {
      _delete(`/user/remove-calendar-account`, this.removePayload)
        .then(async () => {
          const calendarAccountKey = getCalendarAccountKey(
            this.removePayload.email,
            this.removePayload.calendarType
          )
          delete this.authUser.calendarAccounts[calendarAccountKey]
          this.setAuthUser(this.authUser)
          this.removeDialog = false
        })
        .catch((err) => {
          console.error(err)
          this.showError(
            "There was a problem removing this account! Please try again later."
          )
        })
    },
  },

  components: {
    CalendarAccount,
    CalendarTypeSelector,
  },

  watch: {
    calendarEventsMap: {
      immediate: true,
      async handler(newVal) {
        console.log(
          "CalendarAccounts: calendarEventsMap updated",
          Object.keys(newVal)
        )
        // Simply copy the value, don't fetch new data
        this.calendarEventsMapCopy = newVal

        // if (
        //   !this.calendarEventsMap ||
        //   Object.keys(this.calendarEventsMap).length === 0
        // ) {
        //   const timeMin = new Date()
        //   const timeMax = new Date()
        //   try {
        //     this.calendarEventsMapCopy = await get(
        //       `/user/calendars?timeMin=${timeMin.toISOString()}&timeMax=${timeMax.toISOString()}`
        //     )
        //   } catch (err) {
        //     console.error(err)
        //   }
        // } else {
        //   this.calendarEventsMapCopy = this.calendarEventsMap
        // }
      },
    },
  },
}
</script>
